(function () {
  if (window.TSCartSyncCleanup) {
    window.TSCartSyncCleanup();
  }

  const controller = new AbortController();
  const signal = controller.signal;

  const SELECTORS = {
    drawer: 'cart-drawer-component',
    items: 'cart-items-component',
    trigger: '[data-testid="cart-drawer-trigger"]'
  };

  let refreshTimer = null;
  let lastManualCloseAt = 0;

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  async function getCart() {
    const response = await fetch('/cart.js', {
      headers: {
        Accept: 'application/json'
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Could not fetch /cart.js');
    }

    return response.json();
  }

  function updateFallbackCounters(cart) {
    const count = Number(cart && cart.item_count ? cart.item_count : 0);

    document.querySelectorAll('[data-cart-count], .cart-count').forEach(function (el) {
      el.textContent = String(count);
      el.style.display = count > 0 ? '' : 'none';
    });

    document.querySelectorAll('.ts-cart-badge').forEach(function (el) {
      el.textContent = String(count);
      el.style.display = count > 0 ? '' : 'none';
      el.setAttribute('aria-label', count + ' items in cart');
    });

    document.querySelectorAll('.cart-bubble').forEach(function (el) {
      el.style.display = count > 0 ? '' : 'none';

      const textNode = el.querySelector('[aria-hidden="true"], span, .cart-bubble__text-count');
      if (textNode) {
        textNode.textContent = String(count);
      }
    });
  }

  function getDrawer() {
    return document.querySelector(SELECTORS.drawer);
  }

  function getDrawerDialog() {
    const drawer = getDrawer();
    return drawer ? drawer.querySelector('dialog') : null;
  }

  function drawerIsOpen() {
    const drawer = getDrawer();
    if (!drawer) return false;

    return Boolean(
      drawer.querySelector('dialog[open]') ||
      drawer.classList.contains('is-open') ||
      drawer.classList.contains('active')
    );
  }

  function drawerIsClosing() {
    const dialog = getDrawerDialog();
    return Boolean(dialog && dialog.classList.contains('dialog-closing'));
  }

  function clearPendingRefresh() {
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  }

  function markManualClose() {
    lastManualCloseAt = Date.now();
    clearPendingRefresh();
  }

  function isControlWithAction(target, actionPart) {
    const control = target.closest('button, a, [role="button"]');
    if (!control) return false;

    const action = control.getAttribute('on:click') || '';
    return action.indexOf(actionPart) !== -1;
  }

  function isCloseControl(target) {
    const control = target.closest('button, a, [role="button"]');
    if (!control) return false;

    return Boolean(
      control.classList.contains('cart-drawer__close-button') ||
      control.classList.contains('cart-items__empty-button') ||
      control.classList.contains('close-button') ||
      isControlWithAction(target, 'cart-drawer-component/close')
    );
  }

  function isDrawerTrigger(target) {
    return Boolean(target.closest(SELECTORS.trigger));
  }

  function isCartMutationControl(target) {
    return Boolean(
      target.closest('.cart-items__remove') ||
      target.closest('cart-quantity-selector-component') ||
      target.closest('a[href*="/cart/change"]')
    );
  }

  function isCartMutationInput(target) {
    return Boolean(
      target.matches('input[data-cart-line]') ||
      target.matches('input[name="updates[]"]') ||
      target.closest('cart-quantity-selector-component')
    );
  }

  function isBackdropClick(event) {
    const dialog = getDrawerDialog();
    if (!dialog || !dialog.open) return false;
    if (!(event.target instanceof HTMLDialogElement)) return false;

    const rect = dialog.getBoundingClientRect();
    return (
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom
    );
  }

  function openCartDrawer() {
    const drawer = getDrawer();

    if (!drawer || drawerIsOpen() || drawerIsClosing()) {
      return;
    }

    if (typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    const dialog = drawer.querySelector('dialog');

    if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
      dialog.showModal();
      return;
    }

    drawer.classList.add('active', 'is-open');
    document.body.classList.add('overflow-hidden');
  }

  function syncAttribute(target, source, name) {
    const value = source.getAttribute(name);

    if (value === null) {
      target.removeAttribute(name);
    } else {
      target.setAttribute(name, value);
    }
  }

  function updateTriggerFromDocument(doc) {
    const newTrigger = doc.querySelector(SELECTORS.trigger);
    const currentTrigger = document.querySelector(SELECTORS.trigger);

    if (newTrigger && currentTrigger) {
      currentTrigger.innerHTML = newTrigger.innerHTML;
      return true;
    }

    return false;
  }

  function updateDrawerFromDocument(doc) {
    const newDrawer = doc.querySelector(SELECTORS.drawer);
    const currentDrawer = getDrawer();
    let updated = false;

    updateTriggerFromDocument(doc);

    if (newDrawer && currentDrawer) {
      const newDialog = newDrawer.querySelector('dialog');
      const currentDialog = currentDrawer.querySelector('dialog');

      if (newDialog && currentDialog) {
        const wasClosing = currentDialog.classList.contains('dialog-closing');

        currentDialog.className = newDialog.className;
        syncAttribute(currentDialog, newDialog, 'aria-labelledby');
        syncAttribute(currentDialog, newDialog, 'aria-label');

        if (wasClosing) {
          currentDialog.classList.add('dialog-closing');
        }
      }

      const newInner = newDrawer.querySelector('.cart-drawer__inner');
      const currentInner = currentDrawer.querySelector('.cart-drawer__inner');

      if (newInner && currentInner) {
        currentInner.innerHTML = newInner.innerHTML;

        if (typeof currentDrawer.updatedCallback === 'function') {
          currentDrawer.updatedCallback();
        }

        updated = true;
      }
    }

    if (!updated) {
      const newItems = doc.querySelector(SELECTORS.items);
      const currentItems = document.querySelector(SELECTORS.items);

      if (newItems && currentItems) {
        currentItems.replaceWith(newItems);
        updated = true;
      }
    }

    return updated;
  }

  async function refreshDrawerFromFullPage() {
    const url = new URL(window.location.href);
    url.searchParams.set('_ts_cart_refresh', String(Date.now()));

    const response = await fetch(url.toString(), {
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      },
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Could not refresh page HTML for cart drawer.');
    }

    const html = await response.text();
    const doc = parseHTML(html);

    return updateDrawerFromDocument(doc);
  }

  async function refresh(options) {
    options = options || {};

    const requestedAt = options.requestedAt || Date.now();
    const shouldOpen = options.open === true;
    const wasOpen = drawerIsOpen();

    try {
      await refreshDrawerFromFullPage();
    } catch (error) {
      console.warn('TSCartSync full drawer refresh failed:', error);
    }

    const cart = await getCart();
    updateFallbackCounters(cart);

    const closeHappenedAfterRequest = lastManualCloseAt > requestedAt;

    if ((shouldOpen || wasOpen) && !closeHappenedAfterRequest && !drawerIsClosing()) {
      requestAnimationFrame(function () {
        openCartDrawer();
      });
    }

    return cart;
  }

  function scheduleRefresh(open, delay) {
    const requestedAt = Date.now();
    clearPendingRefresh();

    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      refresh({ open: open, requestedAt: requestedAt }).catch(function (error) {
        console.error('TSCartSync refresh failed:', error);
      });
    }, delay || 500);
  }

  async function afterAdd(options) {
    options = options || {};
    return refresh({ open: options.open !== false, requestedAt: Date.now() });
  }

  function attachDrawerCloseListener() {
    const drawer = getDrawer();
    if (!drawer || drawer.dataset.tsCartSyncCloseListener === 'true') return;

    drawer.dataset.tsCartSyncCloseListener = 'true';
    drawer.addEventListener('dialog:close', markManualClose, { signal });
  }

  window.TSCartSync = {
    version: '2.1-stable-drawer-refresh',
    refresh: refresh,
    afterAdd: afterAdd,
    getCart: getCart,
    openCartDrawer: openCartDrawer
  };

  window.TSCartSyncCleanup = function () {
    controller.abort();
    clearPendingRefresh();
  };

  attachDrawerCloseListener();

  document.addEventListener('cart:refresh', function () {
    scheduleRefresh(false, 100);
  }, { signal });

  document.addEventListener('cart:item-added', function () {
    scheduleRefresh(true, 100);
  }, { signal });

  document.addEventListener('cart:updated', function () {
    scheduleRefresh(false, 100);
  }, { signal });

  document.addEventListener('shopify:section:load', attachDrawerCloseListener, { signal });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && drawerIsOpen()) {
      markManualClose();
    }
  }, { capture: true, signal });

  document.addEventListener('click', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (isBackdropClick(event)) {
      markManualClose();
      return;
    }

    if (!target.closest(SELECTORS.drawer)) return;

    if (isCloseControl(target)) {
      markManualClose();
      return;
    }

    if (isDrawerTrigger(target)) return;

    if (isCartMutationControl(target)) {
      scheduleRefresh(false, 900);
    }
  }, { capture: true, signal });

  document.addEventListener('change', function (event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest(SELECTORS.drawer) && isCartMutationInput(target)) {
      scheduleRefresh(false, 900);
    }
  }, { capture: true, signal });

  window.addEventListener('unhandledrejection', function (event) {
    const message = String(
      event.reason && event.reason.message ? event.reason.message : event.reason || ''
    );

    if (message.includes('Section cart-drawer not found')) {
      event.preventDefault();
      scheduleRefresh(true, 150);
    }
  }, { signal });
})();
